import type { ComponentNode, ComponentSchema, SharedNode } from '@api/types.gen'

// A "global" SharedNode is auto-rendered on every page. We inject it by
// prepending/appending a synthetic SharedNodeRef to a page's children at render
// time (built pipe + editor preview) and in the component tree. The synthetic
// nodeId is derived deterministically from the shared node id so:
//   - it never collides with a real node (real ids are nanoids),
//   - the same ref identity is used across the built render, the preview, and
//     the tree, so selection-sync lines up,
//   - gating code (read-only tree rows, right-panel notice) can recognise it.
export const GLOBAL_SHARED_REF_PREFIX = 'global-sn:'

export function globalSharedRefNodeId(sharedNodeId: string): string {
  return `${GLOBAL_SHARED_REF_PREFIX}${sharedNodeId}`
}

export function isGlobalSharedRefNodeId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith(GLOBAL_SHARED_REF_PREFIX)
}

export function sharedNodeIdFromGlobalRef(id: string): string {
  return id.slice(GLOBAL_SHARED_REF_PREFIX.length)
}

type GlobalPosition = 'top' | 'bottom'

function effectivePosition(sn: SharedNode): GlobalPosition {
  return sn.globalPosition === 'bottom' ? 'bottom' : 'top'
}

// A global shared node can opt out of specific pages (per-page exceptions, e.g.
// checkout / plan-selection). Skip injection when the current page is excluded.
// When the current page id is unknown (null), nothing is excluded.
export function isExcludedOnPage(
  sn: SharedNode,
  currentPageId: string | null | undefined,
): boolean {
  return (
    currentPageId != null &&
    Array.isArray(sn.excludedPageIds) &&
    sn.excludedPageIds.includes(currentPageId)
  )
}

function toGlobalRef(sn: SharedNode): ComponentNode {
  return {
    nodeId: globalSharedRefNodeId(sn.id),
    type: 'SharedNodeRef',
    fields: { sharedNodeId: sn.id },
    children: [],
    events: {},
  }
}

// Deep-collect every sharedNodeId already referenced by a SharedNodeRef in the
// given subtree, so we can skip auto-injecting a global that an author has
// already placed manually (it would otherwise render twice).
function collectReferencedSharedNodeIds(children: ComponentNode[], acc: Set<string>): void {
  for (const node of children) {
    if (node.type === 'SharedNodeRef') {
      const refId = node.fields?.sharedNodeId
      if (typeof refId === 'string') acc.add(refId)
    }
    if (node.children?.length) collectReferencedSharedNodeIds(node.children, acc)
  }
}

/**
 * The synthetic SharedNodeRefs for global shared nodes matching `position`,
 * excluding any already referenced manually in `existingChildren`.
 */
export function getGlobalSharedNodeRefs(
  sharedNodes: SharedNode[] | undefined,
  existingChildren: ComponentNode[],
  position: GlobalPosition,
  currentPageId?: string | null,
): ComponentNode[] {
  if (!sharedNodes?.length) return []
  const existing = new Set<string>()
  collectReferencedSharedNodeIds(existingChildren, existing)
  return sharedNodes
    .filter(
      (sn) =>
        sn.isGlobal === true &&
        effectivePosition(sn) === position &&
        !existing.has(sn.id) &&
        !isExcludedOnPage(sn, currentPageId),
    )
    .map(toGlobalRef)
}

/**
 * A page schema with global shared nodes injected as first (`top`) and/or last
 * (`bottom`) children. Returns the original schema reference unchanged when no
 * globals apply, so callers can rely on identity stability.
 */
export function withGlobalSharedNodes(
  pageSchema: ComponentSchema,
  sharedNodes: SharedNode[] | undefined,
  currentPageId?: string | null,
): ComponentSchema {
  const children = pageSchema.children ?? []
  const top = getGlobalSharedNodeRefs(sharedNodes, children, 'top', currentPageId)
  const bottom = getGlobalSharedNodeRefs(sharedNodes, children, 'bottom', currentPageId)
  if (!top.length && !bottom.length) return pageSchema
  return { ...pageSchema, children: [...top, ...children, ...bottom] }
}
